/**
 * Affiliate signup + approval queue — operator UI.
 *
 * Composition:
 *   - KPI strip: today / week / pending / rejected (derived from /queue)
 *   - Status filter dropdown
 *   - Pending approvals table with Approve / Reject actions
 *   - Referral code readonly field + copy button
 *
 * Data layer: TanStack Query for fetch + mutation; Sonner for feedback.
 * Form / reject reason: a lightweight modal-less prompt via `window.prompt`
 * keeps us inside the four-file lane (no extra React state machinery needed
 * for a reason field that is rarely used in the happy path).
 *
 * Brand: emerald→sky headline gradient, zinc borders, 6–8px rounded, light
 * theme only, no emojis, no competitor names.
 */
import { useMemo, useState, useCallback } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	approvalQueryKeys,
	approveAffiliate,
	computeSignupStats,
	fetchApprovalQueue,
	fetchReferralCode,
	formatInteger,
	formatSignupDate,
	isQueueFilter,
	QUEUE_FILTERS,
	type QueueFilter,
	type QueueRow,
	rejectAffiliate,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — kept grouped for JSX readability.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const INPUT_RO =
	"w-full rounded-[6px] border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm font-mono text-zinc-700";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Copy-to-clipboard — defensive against non-https contexts. Returns true on
// success so the caller can surface a toast.
// ---------------------------------------------------------------------------

async function copyToClipboard(text: string): Promise<boolean> {
	try {
		if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// fall through to the textarea fallback
	}
	try {
		if (typeof document === "undefined") return false;
		const ta = document.createElement("textarea");
		ta.value = text;
		ta.style.position = "fixed";
		ta.style.left = "-9999px";
		document.body.appendChild(ta);
		ta.focus();
		ta.select();
		const ok = document.execCommand("copy");
		document.body.removeChild(ta);
		return ok;
	} catch {
		return false;
	}
}

// ---------------------------------------------------------------------------
// Main component — default export so the route file can import it cleanly.
// ---------------------------------------------------------------------------

export interface AffiliateApprovalsPageProps {
	/** Optional initial filter override — the route passes this from ?status= */
	initialStatus?: QueueFilter;
}

export function AffiliateApprovalsPage(props: AffiliateApprovalsPageProps = {}) {
	const queryClient = useQueryClient();
	const [status, setStatus] = useState<QueueFilter>(
		props.initialStatus && isQueueFilter(props.initialStatus)
			? props.initialStatus
			: "pending",
	);

	// ----- Queue fetch -----
	const queueQuery = useQuery({
		queryKey: approvalQueryKeys.queue(status),
		queryFn: () => fetchApprovalQueue({ status }),
	});

	// ----- Referral code fetch (separate key, cached across filter changes) -----
	const referralQuery = useQuery({
		queryKey: approvalQueryKeys.referralCode(),
		queryFn: fetchReferralCode,
	});

	const rows: readonly QueueRow[] = queueQuery.data?.items ?? [];
	const stats = useMemo(() => computeSignupStats(rows), [rows]);

	// ----- Mutations -----
	const approveMutation = useMutation({
		mutationFn: approveAffiliate,
		onSuccess: (_res, id) => {
			toast.success("Affiliate approved.");
			removeRowLocally(queryClient, status, id);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to approve affiliate.");
		},
	});

	const rejectMutation = useMutation({
		mutationFn: rejectAffiliate,
		onSuccess: (_res, vars) => {
			toast.success("Affiliate rejected.");
			removeRowLocally(queryClient, status, vars.id);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to reject affiliate.");
		},
	});

	const busy = approveMutation.isPending || rejectMutation.isPending;

	const handleApprove = useCallback(
		(row: QueueRow) => {
			if (busy) return;
			approveMutation.mutate(row.id);
		},
		[approveMutation, busy],
	);

	const handleReject = useCallback(
		(row: QueueRow) => {
			if (busy) return;
			const reason =
				typeof window !== "undefined"
					? window.prompt("Reason for rejection (min. 3 chars):", "")
					: "";
			if (reason == null) return;
			const trimmed = reason.trim();
			if (trimmed.length < 3) {
				toast.error("Reason must be at least 3 characters.");
				return;
			}
			rejectMutation.mutate({ id: row.id, reason: trimmed });
		},
		[rejectMutation, busy],
	);

	const handleCopyCode = useCallback(async () => {
		const code = referralQuery.data?.code ?? "";
		if (!code) {
			toast.error("No referral code to copy yet.");
			return;
		}
		const ok = await copyToClipboard(code);
		if (ok) toast.success("Referral code copied.");
		else toast.error("Unable to copy referral code.");
	}, [referralQuery.data?.code]);

	const handleStatusChange = useCallback(
		(next: QueueFilter) => {
			if (next === status) return;
			setStatus(next);
		},
		[status],
	);

	// -----------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------
	return (
		<main
			data-testid="affiliate-approvals-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />
			<KpiStrip
				stats={stats}
				loading={queueQuery.isLoading}
				total={rows.length}
			/>
			<ReferralCard
				code={referralQuery.data?.code ?? null}
				loading={referralQuery.isLoading}
				onCopy={handleCopyCode}
			/>
			<QueueCard
				status={status}
				onStatusChange={handleStatusChange}
				rows={rows}
				loading={queueQuery.isLoading}
				error={queueQuery.error?.message ?? null}
				busy={busy}
				onApprove={handleApprove}
				onReject={handleReject}
			/>
		</main>
	);
}

export default AffiliateApprovalsPage;

// ---------------------------------------------------------------------------
// Helpers — cache surgery + sub-components kept near the shell component.
// ---------------------------------------------------------------------------

function removeRowLocally(
	queryClient: ReturnType<typeof useQueryClient>,
	status: QueueFilter,
	removedId: string,
): void {
	queryClient.setQueryData<
		{ items: QueueRow[]; count: number; filters: unknown } | undefined
	>(approvalQueryKeys.queue(status), (prev) => {
		if (!prev) return prev;
		const next = prev.items.filter((row) => row.id !== removedId);
		return { ...prev, items: next, count: next.length };
	});
	// Invalidate the sibling-status caches so a later filter switch re-pulls
	// fresh data reflecting the new state of the affiliate row.
	for (const other of QUEUE_FILTERS) {
		if (other !== status) {
			queryClient.invalidateQueries({
				queryKey: approvalQueryKeys.queue(other),
			});
		}
	}
}

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Affiliates
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Signup & approval queue
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Review incoming affiliate applications, approve or reject in one click,
				and share the tenant referral code that ties a new signup back to its
				referring partner.
			</p>
		</header>
	);
}

function KpiStrip(props: {
	stats: { today: number; week: number; pending: number; rejected: number };
	loading: boolean;
	total: number;
}) {
	const cells = [
		{ label: "Today", value: props.stats.today },
		{ label: "This week", value: props.stats.week },
		{ label: "Pending", value: props.stats.pending },
		{ label: "Rejected", value: props.stats.rejected },
	];
	return (
		<section
			aria-label="Signup statistics"
			className="grid grid-cols-2 gap-3 sm:grid-cols-4"
			data-testid="kpi-strip"
		>
			{cells.map((c) => (
				<div key={c.label} className={`${CARD} flex flex-col gap-1 p-4`}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{c.label}
					</p>
					<p className="text-2xl font-semibold tabular-nums text-zinc-900">
						{props.loading ? "—" : formatInteger(c.value)}
					</p>
				</div>
			))}
		</section>
	);
}

function ReferralCard(props: {
	code: string | null;
	loading: boolean;
	onCopy: () => void;
}) {
	const display = props.loading
		? "Loading referral code."
		: (props.code ?? "No referral code minted yet.");
	return (
		<section
			aria-label="Referral code"
			className={`${CARD} flex flex-col gap-3 p-5 sm:flex-row sm:items-end`}
			data-testid="referral-card"
		>
			<div className="flex flex-1 flex-col gap-1.5">
				<label htmlFor="affiliate-referral-code" className={LABEL}>
					Tenant referral code
				</label>
				<input
					id="affiliate-referral-code"
					name="referralCode"
					type="text"
					readOnly
					className={INPUT_RO}
					value={display}
					aria-readonly="true"
				/>
				<p className="text-xs text-zinc-500">
					Share this code so new affiliates are linked to the referring partner
					on signup.
				</p>
			</div>
			<button
				type="button"
				onClick={props.onCopy}
				disabled={props.loading || !props.code}
				className={BTN_NEUTRAL}
				data-testid="referral-copy-btn"
			>
				Copy code
			</button>
		</section>
	);
}

function QueueCard(props: {
	status: QueueFilter;
	onStatusChange: (next: QueueFilter) => void;
	rows: readonly QueueRow[];
	loading: boolean;
	error: string | null;
	busy: boolean;
	onApprove: (row: QueueRow) => void;
	onReject: (row: QueueRow) => void;
}) {
	return (
		<section className={`${CARD} flex flex-col`}>
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						Pending approvals
					</h2>
					<p className="text-sm text-zinc-500">
						{props.rows.length === 0
							? "Queue is clear for this filter."
							: `${props.rows.length} application${props.rows.length === 1 ? "" : "s"} in view.`}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<label
						htmlFor="approvals-status-filter"
						className="text-sm text-zinc-700"
					>
						Status
					</label>
					<select
						id="approvals-status-filter"
						name="status"
						className={SELECT}
						value={props.status}
						onChange={(e) =>
							props.onStatusChange(e.currentTarget.value as QueueFilter)
						}
						data-testid="status-filter"
					>
						{QUEUE_FILTERS.map((f) => (
							<option key={f} value={f}>
								{f.charAt(0).toUpperCase() + f.slice(1)}
							</option>
						))}
					</select>
				</div>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading applications.</div>
			) : props.rows.length === 0 ? (
				<EmptyState status={props.status} />
			) : (
				<QueueTable
					rows={props.rows}
					busy={props.busy}
					onApprove={props.onApprove}
					onReject={props.onReject}
				/>
			)}
		</section>
	);
}

function QueueTable(props: {
	rows: readonly QueueRow[];
	busy: boolean;
	onApprove: (row: QueueRow) => void;
	onReject: (row: QueueRow) => void;
}) {
	return (
		<div className="overflow-x-auto">
			<table
				className="w-full border-collapse text-left text-sm"
				data-testid="approvals-table"
			>
				<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
					<tr>
						<Th>ID</Th>
						<Th>Email</Th>
						<Th>Company</Th>
						<Th>Country</Th>
						<Th>Signup</Th>
						<Th className="text-right">Actions</Th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-200">
					{props.rows.map((row) => (
						<tr
							key={row.id}
							data-testid={`approval-row-${row.id}`}
							className="hover:bg-zinc-50/80"
						>
							<Td mono>
								<span className="max-w-[9rem] truncate text-zinc-600">
									{row.id}
								</span>
							</Td>
							<Td>
								<div className="flex flex-col">
									<span className="font-medium text-zinc-900">{row.email}</span>
									{row.name && (
										<span className="text-xs text-zinc-500">{row.name}</span>
									)}
								</div>
							</Td>
							<Td>{row.company ?? "—"}</Td>
							<Td>{row.country ?? "—"}</Td>
							<Td>{formatSignupDate(row.created_at)}</Td>
							<Td className="text-right">
								<div className="inline-flex gap-2">
									<button
										type="button"
										onClick={() => props.onApprove(row)}
										disabled={props.busy || row.status !== "pending"}
										className={BTN_PRIMARY}
										data-testid={`approve-${row.id}`}
									>
										Approve
									</button>
									<button
										type="button"
										onClick={() => props.onReject(row)}
										disabled={props.busy || row.status !== "pending"}
										className={BTN_DANGER}
										data-testid={`reject-${row.id}`}
									>
										Reject
									</button>
								</div>
							</Td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function EmptyState(props: { status: QueueFilter }) {
	const hint =
		props.status === "pending"
			? "No pending applications right now."
			: props.status === "approved"
				? "No approved affiliates in this view."
				: props.status === "rejected"
					? "No rejected applications on file."
					: "No affiliate applications to display.";
	return (
		<div className="flex flex-col items-center gap-1 p-10 text-center">
			<p className="text-sm font-medium text-zinc-700">{hint}</p>
			<p className="text-xs text-zinc-500">
				New applications appear here automatically as operators submit them.
			</p>
		</div>
	);
}

// ---- tiny table primitives --------------------------------------------------

function Th(props: { children: React.ReactNode; className?: string }) {
	return (
		<th
			scope="col"
			className={`px-4 py-2.5 ${props.className ?? ""}`.trim()}
		>
			{props.children}
		</th>
	);
}

function Td(props: {
	children: React.ReactNode;
	className?: string;
	mono?: boolean;
}) {
	const base = "px-4 py-3 align-top text-zinc-700";
	const cls = [base, props.mono ? "font-mono text-xs" : "", props.className ?? ""]
		.filter(Boolean)
		.join(" ");
	return <td className={cls}>{props.children}</td>;
}
